// LivestreamDetailStreamLabel.jsx
import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';

import Animator from '../component/Animator.jsx';

import {
  privateShowStatus as privateShowStatusConstant,
  PRESET_PREVIEW,
  PRESET_SD,
  ExclusivePrivateStatus,
} from '../resource/liveStreamConstants.js';
import getCurrentUnixTimestamp from '../resource/getCurrentUnixTimestamp.js';
import { formatTimeStringBySec } from '../resource/formatTimeString.js';
import { TranslationNamespace } from '../resource/translationNamespace.js';

import { color } from '../style/variables.js';
import OpacityBreath from '../style/OpacityBreath.js';

export const LivestreamDetailStreamLabel = ({
  streamStatus = null,
  privateShowStatus = null,
  exclusivePrivateStatus = null,
  timestampOffsetSeconds = 0,
  privateStreamCounterExp = 0,
  privateLiveStreamStartTimestamp = 0,
  countdownToExclusivePrivateNbf = 0,
  isMeExclusivePrivateInviteInitiator = false,
  consideringExclusivePrivateExp = 0,
}) => {
  const { t } = useTranslation();
  const renderTimer = useCallback(
    ({ i18nText = '', countdownExp = 0 }) => {
      const now = getCurrentUnixTimestamp({
        offsetSeconds: timestampOffsetSeconds,
      });
      const countdownSeconds = Math.max(countdownExp - now, 0);
      return (
        <StreamingLabelText shouldBreath={countdownSeconds <= 30}>
          {t(i18nText, {
            timer: formatTimeStringBySec(countdownSeconds),
            ns: TranslationNamespace.LIVESTREAM,
          })}
        </StreamingLabelText>
      );
    },
    [t, timestampOffsetSeconds]
  );

  // 1-1 invite sent (CONSIDERING): show the initiator a countdown while waiting for the streamer to respond.
  if (
    exclusivePrivateStatus === ExclusivePrivateStatus.CONSIDERING &&
    isMeExclusivePrivateInviteInitiator
  ) {
    return (
      <StyledLivestreamDetailStreamLabel>
        <Animator
          render={() => {
            const now = getCurrentUnixTimestamp({
              offsetSeconds: timestampOffsetSeconds,
            });
            const countdownSeconds = Math.max(
              consideringExclusivePrivateExp - now,
              0
            );
            return (
              <StreamingLabelText shouldBreath={countdownSeconds <= 30}>
                {t('waiting_trigger_respond', {
                  countdown: formatTimeStringBySec(countdownSeconds),
                  ns: TranslationNamespace.LIVESTREAM,
                })}
              </StreamingLabelText>
            );
          }}
          fps={1}
        />
      </StyledLivestreamDetailStreamLabel>
    );
  }
  if (streamStatus === PRESET_SD) {
    if (
      privateShowStatus === privateShowStatusConstant.PREPARING ||
      privateShowStatus === privateShowStatusConstant.PERFORMING
    ) {
      return (
        <StyledLivestreamDetailStreamLabel>
          <StreamingLabelText>
            {t('badge_live_performing', {
              ns: TranslationNamespace.LIVESTREAM,
            })}
          </StreamingLabelText>
        </StyledLivestreamDetailStreamLabel>
      );
    }
    if (
      exclusivePrivateStatus === ExclusivePrivateStatus.ACCEPTED ||
      exclusivePrivateStatus === ExclusivePrivateStatus.PREPARING
    ) {
      return (
        <StyledLivestreamDetailStreamLabel>
          <Animator
            render={() =>
              renderTimer({
                i18nText: 'title_stream_convert_exclusive',
                countdownExp: countdownToExclusivePrivateNbf,
              })
            }
            fps={1}
          />
        </StyledLivestreamDetailStreamLabel>
      );
    }
    if (exclusivePrivateStatus === ExclusivePrivateStatus.PERFORMING) {
      return (
        <StyledLivestreamDetailStreamLabel>
          <StreamingLabelText>
            {t('title_stream_exclusive', {
              ns: TranslationNamespace.LIVESTREAM,
            })}
          </StreamingLabelText>
        </StyledLivestreamDetailStreamLabel>
      );
    }

    return (
      <StyledLivestreamDetailStreamLabel>
        <StreamingLabelText>
          {t('title_stream_private', {
            ns: TranslationNamespace.LIVESTREAM,
          })}
        </StreamingLabelText>
      </StyledLivestreamDetailStreamLabel>
    );
  }
  if (streamStatus === PRESET_PREVIEW) {
    const countdownExp =
      privateLiveStreamStartTimestamp || privateStreamCounterExp;
    return (
      <StyledLivestreamDetailStreamLabel>
        {countdownExp ? (
          <Animator
            render={() =>
              renderTimer({
                i18nText: 'title_countdown_to_private_stream_timer',
                countdownExp,
              })
            }
            fps={1}
          />
        ) : (
          <StreamingLabelText>
            {t('title_stream_public', {
              ns: TranslationNamespace.LIVESTREAM,
            })}
          </StreamingLabelText>
        )}
      </StyledLivestreamDetailStreamLabel>
    );
  }
  return null;
};

LivestreamDetailStreamLabel.propTypes = {
  streamStatus: PropTypes.string,
  privateShowStatus: PropTypes.string,
  exclusivePrivateStatus: PropTypes.string,
  timestampOffsetSeconds: PropTypes.number,
  privateStreamCounterExp: PropTypes.number,
  privateLiveStreamStartTimestamp: PropTypes.number,
  countdownToExclusivePrivateNbf: PropTypes.number,
  isMeExclusivePrivateInviteInitiator: PropTypes.bool,
  consideringExclusivePrivateExp: PropTypes.number,
};

const StyledLivestreamDetailStreamLabel = styled.span`
  padding: 0px 16px;
  display: inline-flex;
  align-items: center;
  user-select: none;
  color: ${color.neutral[100]};
  background: linear-gradient(
    90deg,
    rgba(71, 71, 71, 0) 0%,
    rgba(71, 71, 71, 0.2) 25%,
    rgba(71, 71, 71, 0.4) 50%,
    rgba(71, 71, 71, 0.2) 75%,
    rgba(71, 71, 71, 0) 100%
  );
`;

const StreamingLabelText = styled.span`
  font-size: 12px;
  font-weight: 400;
  line-height: 1.5;
  user-select: none;
  ${({ shouldBreath }) => {
    if (shouldBreath) {
      return OpacityBreath();
    }
  }}
`;

export default LivestreamDetailStreamLabel;
